import Decimal from 'decimal.js';
import { compact, groupBy, uniq } from 'lodash';

import { AfterDeath, EstateWaterfallEdgeKind } from '@/types/schema';
import { getProvisionsByDeathForScenario } from '@/utils/dispositiveProvisions';
import { getNodes } from '@/utils/graphqlUtils';

import {
  GetDispositiveProvisions_EstateWaterfallFragment,
  GetDispositiveProvisions_EstateWaterfallVizEdgeFragment,
  GetDispositiveProvisions_EstateWaterfallVizNodeFragment,
} from '../DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import {
  DispositiveProvisions_DispositionScenarioFragment,
  DispositiveProvisions_DispositiveProvisionFragment,
} from '../graphql/DispositiveProvisions.fragments.generated';
import {
  DispositiveProvisionDirection,
  DispositiveProvisionWithEdges,
  UponDeath,
} from './DispositiveProvisionsByDeath.types';

function getFirstDeathFilter(
  id: string
): (edge: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment) => boolean {
  return (
    edge: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment
  ): boolean => {
    if (edge.to.id !== id) return false;

    if (edge.kind === EstateWaterfallEdgeKind.Disposition)
      return edge.to.afterDeath === AfterDeath.First;

    if (edge.kind === EstateWaterfallEdgeKind.Pourover)
      return edge.to.afterDeath === AfterDeath.None;
    return false;
  };
}

function getSecondDeathFilter(
  id: string
): (edge: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment) => boolean {
  return (
    edge: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment
  ): boolean => {
    if (edge.to.id !== id) return false;

    if (edge.kind === EstateWaterfallEdgeKind.Disposition)
      return edge.to.afterDeath === AfterDeath.Second;

    if (edge.kind === EstateWaterfallEdgeKind.Pourover)
      return edge.to.afterDeath === AfterDeath.First;
    return false;
  };
}

/**
 * since provisions are always described as outbound, use data from the source vizNode
 * to repopulate a temporary provision with the right data
 */
export function getProvisionSourceFromAssociatedVizNode(
  provision: DispositiveProvisions_DispositiveProvisionFragment,
  vizNode: GetDispositiveProvisions_EstateWaterfallVizNodeFragment | undefined
): DispositiveProvisions_DispositiveProvisionFragment {
  const newProvision = {
    ...provision,
  };

  delete newProvision.individual;
  delete newProvision.entity;
  delete newProvision.testamentaryEntity;
  delete newProvision.organization;

  // since all the fields are selected as a type `Node`, all will always be present and
  // have their common `.id` field set, regardless of the resolved type; instead, check the
  // `__typename` field to narrow to the actual type of the node, and then use that value
  if (vizNode?.testamentaryEntity.__typename === 'TestamentaryEntity') {
    newProvision.testamentaryEntity = vizNode.testamentaryEntity;
  } else if (vizNode?.entity.__typename === 'Entity') {
    newProvision.entity = vizNode.entity;
  } else if (vizNode?.individual.__typename === 'ClientProfile') {
    newProvision.individual = vizNode.individual;
  } else if (vizNode?.organization.__typename === 'ClientOrganization') {
    newProvision.organization = vizNode.organization;
  } else {
    throw new Error(
      `No source found for provision ${provision.id} on vizNode ${vizNode?.id}`
    );
  }

  return newProvision;
}

export function getCaptionText(
  direction: DispositiveProvisionDirection,
  hasAnyProvisionsInView: boolean
): string {
  if (!hasAnyProvisionsInView) {
    if (direction === DispositiveProvisionDirection.Receiving) {
      return 'This entity does not receive any distributions.';
    } else {
      return 'This entity does not make any distributions.';
    }
  }

  if (direction === DispositiveProvisionDirection.Receiving) {
    return 'This entity receives the following distributions.';
  } else {
    return 'This entity distributes to the following entities and people.';
  }
}

/**
 * Deduplicates receiving provisions by disposition ID AND source entity,
 * aggregating values. The backend creates one edge entry per asset on a
 * disposition, so a single DP with multiple assets appears multiple times
 * with per-asset values. We group by both disposition ID and source (from)
 * node because pro rata templates share the same provision IDs across
 * multiple source entities — grouping by disposition ID alone would collapse
 * distinct source entities into a single row.
 */
function deduplicateReceivingProvisions(
  provisions: DispositiveProvisionWithEdges[]
): DispositiveProvisionWithEdges[] {
  const grouped = groupBy(
    provisions,
    (p) => `${p.dispositiveProvision.id}|${p.from?.id ?? ''}`
  );
  return Object.values(grouped).map((group) => {
    // Safe to assert — groupBy always produces non-empty groups
    const first = group[0]!;
    if (group.length === 1) return first;

    const totalValue = group.reduce(
      (acc, p) => acc.plus(p.value ?? new Decimal(0)),
      new Decimal(0)
    );
    return { ...first, value: totalValue };
  });
}

function mapWaterfallEdgesToDispositiveProvisionWithEdges(
  edges: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment[]
): DispositiveProvisionWithEdges[] {
  return edges.reduce((acc, edge) => {
    const isPourover = edge.kind === EstateWaterfallEdgeKind.Pourover;
    const provisions = isPourover
      ? edge.associatedPouroverDispositions
      : edge.associatedDispositiveProvisions;

    const provisionsWithEdges = compact(
      provisions ?? []
    ).map<DispositiveProvisionWithEdges>(({ dispositiveProvision, value }) => ({
      dispositiveProvision,
      value,
      from: edge.from,
      to: edge.to,
    }));

    return acc.concat(provisionsWithEdges);
  }, [] as DispositiveProvisionWithEdges[]);
}

/**
 * Filters `waterfallEdges` to those referencing `provisionId` (across both
 * dispositions and pourovers), prefers edges originating from `currentNodeId`
 * when any exist, and sums their value. Shared by `getDispositionsFromScenario`
 * and `getCalculatedValueForProvision` so the per-provision edge-aggregation
 * semantics live in one place.
 */
function aggregateProvisionEdges(
  provisionId: string,
  waterfallEdges: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment[],
  currentNodeId?: string
): {
  relatedEdges: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment[];
  totalValue: Decimal;
} {
  // Find *all* edges that reference this dispositive provision
  const allEdgesForProvision = waterfallEdges.filter((edge) => {
    const foundInDispositions = edge.associatedDispositiveProvisions?.some(
      (adp) => adp?.dispositiveProvision?.id === provisionId
    );
    const foundInPourovers = edge.associatedPouroverDispositions?.some(
      (pdp) => pdp?.dispositiveProvision?.id === provisionId
    );
    return foundInDispositions || foundInPourovers;
  });

  let relatedEdges = allEdgesForProvision;

  if (currentNodeId) {
    const hasOwnEdges = allEdgesForProvision.some(
      (edge) => edge.from.id === currentNodeId
    );

    if (hasOwnEdges) {
      // Only keep edges originating from the current node if any exist
      relatedEdges = allEdgesForProvision.filter(
        (edge) => edge.from.id === currentNodeId
      );
    }
  }

  // Aggregate the total value across all matching edges
  const totalValue = relatedEdges.reduce(
    (acc, edge) => acc.plus(edge.value ?? new Decimal(0)),
    new Decimal(0)
  );

  return { relatedEdges, totalValue };
}

function getDispositionsFromScenario(
  provisionList:
    | DispositiveProvisions_DispositionScenarioFragment['dispositiveProvisions']
    | undefined,
  waterfallEdges: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment[],
  showCalculatedDispositions: boolean,
  currentNodeId?: string,
  proRataTemplateName?: string
): DispositiveProvisionWithEdges[] {
  const dispositiveProvisions = getNodes(provisionList)
    .sort((a, b) => a.dispositionOrder - b.dispositionOrder)
    .map((dispositiveProvision) => {
      const { relatedEdges, totalValue } = aggregateProvisionEdges(
        dispositiveProvision.id,
        waterfallEdges,
        currentNodeId
      );

      // Use the first edge (if any) to capture `from` / `to` metadata – they should all be identical for a single disposition
      const representativeEdge = relatedEdges[0];

      return {
        dispositiveProvision,
        value: totalValue,
        from: representativeEdge?.from,
        to: representativeEdge?.to,
        ...(proRataTemplateName ? { proRataTemplateName } : {}),
      };
    });

  const groupedProvisions = groupBy(dispositiveProvisions, (p) => {
    return p.to?.id ?? p.dispositiveProvision.id;
  });

  // Deduplicate outbound dispositions to the same recipient (e.g., when an individual has multiple dispositions from the same source)
  // so when we show the calculated dispositions, we can use the disposition edge to determine the aggregate value of those dispositions
  if (showCalculatedDispositions) {
    return Object.entries(groupedProvisions).flatMap(([_, provisions]) => {
      const dispositiveProvision = provisions[0];
      if (!dispositiveProvision) {
        return [];
      }

      return {
        ...dispositiveProvision,
        transferTaxKinds: uniq(
          provisions.map((p) => p.dispositiveProvision.transferTaxKind)
        ),
      };
    });
  }

  return dispositiveProvisions;
}

export function getDispositiveProvisionsWithEdgesByDeath(
  waterfallEdges: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment[],
  currentNodeId: string,
  direction: DispositiveProvisionDirection,
  dispositionScenario?:
    | DispositiveProvisions_DispositionScenarioFragment
    | undefined,
  showCalculatedDispositions = false
): Record<UponDeath, DispositiveProvisionWithEdges[]> {
  if (direction === DispositiveProvisionDirection.Receiving) {
    const waterfallEdgesForId: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment[] =
      waterfallEdges.filter((edge) => {
        if (edge.from.id !== currentNodeId && edge.to.id !== currentNodeId)
          return false;

        if (
          edge.kind === EstateWaterfallEdgeKind.Disposition &&
          !!edge.associatedDispositiveProvisions
        ) {
          return true;
        }

        if (
          edge.kind === EstateWaterfallEdgeKind.Pourover &&
          !!edge.associatedPouroverDispositions
        ) {
          return true;
        }

        return false;
      });

    // Receiving edges are represented in the viz nodes after the death, so look ahead to the next death.
    // Deduplicate because the backend creates one edge entry per asset on a disposition.
    return {
      uponFirstDeath: deduplicateReceivingProvisions(
        mapWaterfallEdgesToDispositiveProvisionWithEdges(
          waterfallEdgesForId.filter(getFirstDeathFilter(currentNodeId))
        )
      ),
      uponSecondDeath: deduplicateReceivingProvisions(
        mapWaterfallEdgesToDispositiveProvisionWithEdges(
          waterfallEdgesForId.filter(getSecondDeathFilter(currentNodeId))
        )
      ),
    };
  }

  const { firstDeathProvisions, secondDeathProvisions } =
    getProvisionsByDeathForScenario(dispositionScenario);

  // Determine pro rata template names for first and second death provisions
  const firstDeathProRataTemplateName = dispositionScenario
    ?.dispositiveProvisionsTemplate?.proRata
    ? dispositionScenario.dispositiveProvisionsTemplate.displayName
    : undefined;
  const secondDeathProRataTemplateName = dispositionScenario
    ?.secondDeathDispositiveProvisionsTemplate?.proRata
    ? dispositionScenario.secondDeathDispositiveProvisionsTemplate.displayName
    : undefined;

  // ...while distributing edges are represented in the viz nodes before the death and get sorted
  return {
    uponFirstDeath: getDispositionsFromScenario(
      firstDeathProvisions,
      waterfallEdges,
      showCalculatedDispositions,
      currentNodeId,
      firstDeathProRataTemplateName
    ),
    uponSecondDeath: getDispositionsFromScenario(
      secondDeathProvisions,
      waterfallEdges,
      showCalculatedDispositions,
      currentNodeId,
      secondDeathProRataTemplateName
    ),
  };
}

/**
 * Aggregates the calculated waterfall value flowing out of `currentNodeId` for a
 * single dispositive provision. Mirrors the per-provision edge aggregation in
 * `getDispositionsFromScenario`, but keyed by provision ID so callers that don't
 * have a death-scoped scenario (e.g. beneficiary designations) can look up
 * calculated amounts. Returns `Decimal(0)` when no edges reference the provision,
 * matching how DP rows render $0 for a recipient that receives nothing.
 */
export function getCalculatedValueForProvision(
  provisionId: string,
  waterfallEdges: GetDispositiveProvisions_EstateWaterfallVizEdgeFragment[],
  currentNodeId: string
): Decimal {
  const { totalValue } = aggregateProvisionEdges(
    provisionId,
    waterfallEdges,
    currentNodeId
  );

  return totalValue;
}

export function getSourceNodeTaxDue(
  nodeId: string,
  afterDeath: AfterDeath,
  waterfall: GetDispositiveProvisions_EstateWaterfallFragment
): {
  estateTax: Decimal;
  gstTax: Decimal;
} {
  // need to back up one death to get the tax due, since
  // the node may not exist after death
  const afterDeathToCheck =
    afterDeath === AfterDeath.First ? AfterDeath.None : AfterDeath.First;

  const node = waterfall.visualizationWithProjections.nodes?.find(
    (n: GetDispositiveProvisions_EstateWaterfallVizNodeFragment) =>
      n.id === nodeId && n.afterDeath === afterDeathToCheck
  );

  return {
    estateTax: node?.transferredToEstateTax ?? new Decimal(0),
    gstTax: node?.transferredToGiftingTax ?? new Decimal(0),
  };
}
